import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { groundTruthService } from '../../services';
import { authFetch } from '../../services/authService';
import { GroundTruthDocument } from '../../types';
import { GroundTruthViewerModal } from '../../components/admin/GroundTruthViewerModal';
import { GroundTruthImportModal } from '../../components/admin/GroundTruthImportModal';
import { GroundTruthEditFieldModal } from '../../components/admin/GroundTruthEditFieldModal';
import { useAuth } from '../../contexts/AuthContext';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef, FilterableTableHandle } from '../../components/ui';
import { useBulkSelectionScope } from '../../hooks/useBulkSelectionScope';
import toast from 'react-hot-toast';
import { PageContainer } from '../../components/layout';

// Inline tag editor component
function InlineTagEditor({
  tags,
  allTags,
  onSave,
}: {
  tags: string[];
  allTags: string[];
  onSave: (tags: string[]) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [localTags, setLocalTags] = useState<string[]>(tags);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSuggestions = allTags.filter(
    (t) => t.toLowerCase().includes(inputValue.toLowerCase()) && !localTags.includes(t)
  );

  const addTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed && !localTags.includes(trimmed)) {
      const updated = [...localTags, trimmed];
      setLocalTags(updated);
      onSave(updated);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tag: string) => {
    const updated = localTags.filter((t) => t !== tag);
    setLocalTags(updated);
    onSave(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Escape') {
      setEditing(false);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="flex flex-wrap gap-1 cursor-pointer min-h-[24px]"
        onClick={() => { setEditing(true); setLocalTags(tags); setTimeout(() => inputRef.current?.focus(), 50); }}
        title="Cliquer pour éditer les tags"
      >
        {tags.length > 0 ? tags.map((tag) => (
          <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            {tag}
          </span>
        )) : (
          <span className="text-gray-300 text-xs">+</span>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 items-center">
        {localTags.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            {tag}
            <button onClick={() => removeTag(tag)} className="text-indigo-400 hover:text-indigo-600">&times;</button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => { setEditing(false); setShowSuggestions(false); }, 200)}
          placeholder="tag..."
          className="border-none outline-none text-xs w-16 bg-transparent"
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-32 overflow-y-auto">
          {filteredSuggestions.map((tag) => (
            <button
              key={tag}
              onMouseDown={() => addTag(tag)}
              className="block w-full text-left px-3 py-1 text-xs hover:bg-indigo-50"
            >
              {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper to compute statut value for filtering
function getStatut(doc: GroundTruthDocument): string[] {
  const statuts: string[] = [];
  if (doc.needs_review) statuts.push('needs_review');
  if (doc.skip_benchmark) statuts.push('skip_benchmark');
  if (!doc.needs_review && !doc.skip_benchmark) statuts.push('ok');
  return statuts;
}

export const GroundTruthPage: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin;
  const [documents, setDocuments] = useState<GroundTruthDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<GroundTruthDocument | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Table ref & counts
  const tableRef = useRef<FilterableTableHandle>(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const handleFilteredCountChange = useCallback((filtered: number, total: number) => {
    setFilteredCount(filtered);
    setTotalCount(total);
  }, []);
  const hasActiveTableFilters = filteredCount < totalCount;

  // Selection
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const [bulkTagInput, setBulkTagInput] = useState('');
  const bulkTagRef = useRef<HTMLDivElement>(null);
  const bulkRemoveTagRef = useRef<HTMLDivElement>(null);
  const [showBulkTagDropdown, setShowBulkTagDropdown] = useState<'add' | 'remove' | null>(null);

  // Modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [editFieldDoc, setEditFieldDoc] = useState<GroundTruthDocument | null>(null);

  // Close bulk tag dropdowns on click outside
  useEffect(() => {
    if (!showBulkTagDropdown) return;
    const handler = (e: MouseEvent) => {
      if (
        bulkTagRef.current && !bulkTagRef.current.contains(e.target as Node) &&
        bulkRemoveTagRef.current && !bulkRemoveTagRef.current.contains(e.target as Node)
      ) {
        setShowBulkTagDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showBulkTagDropdown]);

  useEffect(() => {
    loadDocuments();
    loadTags();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await groundTruthService.list();
      setDocuments(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const tags = await groundTruthService.listDistinctTags();
      setAllTags(tags);
    } catch {
      setAllTags([]);
    }
  };

  const handleToggleFlag = async (
    doc: GroundTruthDocument,
    flag: 'needs_review' | 'skip_benchmark'
  ) => {
    try {
      await groundTruthService.updateFlags(Number(doc.id), {
        [flag]: !doc[flag],
      });
      toast.success(`Flag "${flag}" mis à jour`);
      loadDocuments();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur';
      toast.error(message);
    }
  };

  const handleUpdateTags = async (doc: GroundTruthDocument, newTags: string[]) => {
    try {
      await groundTruthService.updateTags(Number(doc.id), newTags);
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, tags: newTags } : d))
      );
      loadTags();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur';
      toast.error(message);
    }
  };

  const handleDelete = async (doc: GroundTruthDocument) => {
    if (!window.confirm(`Supprimer "${doc.filename}" ? Cette action est irréversible.`)) {
      return;
    }
    try {
      await groundTruthService.deleteDocument(Number(doc.id));
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  const handleDownloadPdf = (doc: GroundTruthDocument) => {
    const url = groundTruthService.getDownloadUrl(Number(doc.id));
    authFetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Erreur de téléchargement');
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Erreur lors du téléchargement du PDF'));
  };

  // --- Bulk actions ---
  // Les actions bulk reçoivent la liste des clés à traiter (fournie par le hook
  // useBulkSelectionScope : soit toute la sélection, soit les visibles seulement
  // quand une partie est hors du filtre courant).
  const docsForKeys = (keys: Array<string | number>) => {
    const set = new Set(keys);
    return documents.filter((d) => set.has(d.id));
  };
  const getSelectedDocs = () => documents.filter((d) => selectedKeys.has(d.id));

  const handleBulkFlag = async (keys: Array<string | number>, flag: 'needs_review' | 'skip_benchmark', value: boolean) => {
    const docs = docsForKeys(keys);
    if (docs.length === 0) return;
    try {
      for (const doc of docs) {
        if (doc[flag] !== value) {
          await groundTruthService.updateFlags(Number(doc.id), { [flag]: value });
        }
      }
      toast.success(`${docs.length} document(s) mis à jour`);
      loadDocuments();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erreur');
    }
  };

  const handleBulkDelete = async (keys: Array<string | number>) => {
    const docs = docsForKeys(keys);
    if (docs.length === 0) return;
    if (!window.confirm(`Supprimer ${docs.length} document(s) ? Cette action est irréversible.`)) return;
    try {
      for (const doc of docs) {
        await groundTruthService.deleteDocument(Number(doc.id));
      }
      toast.success(`${docs.length} document(s) supprimé(s)`);
      loadDocuments();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erreur');
    }
  };

  const handleBulkAddTag = async (keys: Array<string | number>, tag: string) => {
    const docs = docsForKeys(keys);
    if (docs.length === 0 || !tag.trim()) return;
    const trimmed = tag.trim();
    try {
      for (const doc of docs) {
        const currentTags = doc.tags || [];
        if (!currentTags.includes(trimmed)) {
          await groundTruthService.updateTags(Number(doc.id), [...currentTags, trimmed]);
        }
      }
      toast.success(`Tag "${trimmed}" ajouté à ${docs.length} document(s)`);
      setBulkTagInput('');
      setShowBulkTagDropdown(null);
      loadDocuments();
      loadTags();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erreur');
    }
  };

  const handleBulkRemoveTag = async (keys: Array<string | number>, tag: string) => {
    const docs = docsForKeys(keys);
    if (docs.length === 0) return;
    try {
      for (const doc of docs) {
        const currentTags = doc.tags || [];
        if (currentTags.includes(tag)) {
          await groundTruthService.updateTags(Number(doc.id), currentTags.filter((t) => t !== tag));
        }
      }
      toast.success(`Tag "${tag}" retiré de ${docs.length} document(s)`);
      setShowBulkTagDropdown(null);
      loadDocuments();
      loadTags();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erreur');
    }
  };

  // All tags present on any selected doc (for remove tag dropdown)
  const selectedDocsTags = useMemo(() => {
    const docs = getSelectedDocs();
    const all = new Set<string>();
    for (const doc of docs) {
      for (const t of (doc.tags || [])) {
        all.add(t);
      }
    }
    return Array.from(all).sort();
  }, [selectedKeys, documents]);

  const hasSelection = selectedKeys.size > 0;
  const hasSelectionWithTags = hasSelection && selectedDocsTags.length > 0;

  // Garde bulk : si des docs sélectionnés sont hors du filtre courant, un modal
  // propose « Tous / Visibles seulement ». Sinon exécution directe.
  const { requestBulk, modal: bulkScopeModal } = useBulkSelectionScope(selectedKeys, tableRef);

  // Tags de runs distincts (union des run_tags de tous les docs) → filterOptions
  // de la colonne « Tags des runs ».
  const allRunTags = useMemo(
    () => Array.from(new Set(documents.flatMap((d) => d.run_tags || []))).sort(),
    [documents]
  );

  const columns: FilterableColumnDef<GroundTruthDocument>[] = useMemo(() => [
    {
      key: 'filename',
      header: 'Fichier',
      sortable: true,
      searchable: true,
      render: (doc) => doc.filename,
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      searchable: true,
      render: (doc) => doc.domain,
    },
    {
      key: 'tags',
      header: 'Tags',
      filterable: true,
      filterValueFn: (doc) => doc.tags || [],
      render: (doc) => (
        isAdmin() ? (
          <InlineTagEditor
            tags={doc.tags || []}
            allTags={allTags}
            onSave={(tags) => handleUpdateTags(doc, tags)}
          />
        ) : (
          <div className="flex flex-wrap gap-1">
            {(doc.tags || []).map((tag) => (
              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                {tag}
              </span>
            ))}
          </div>
        )
      ),
    },
    {
      key: 'run_tags',
      header: 'Tags des runs',
      filterable: true,
      filterValueFn: (doc) => doc.run_tags || [],
      filterOptions: allRunTags.map((t) => ({ value: t, label: t })),
      render: (doc) => (
        (doc.run_tags || []).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {(doc.run_tags || []).map((t) => (
              <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {t}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        )
      ),
    },
    {
      key: 'pdf',
      header: 'PDF',
      align: 'center' as const,
      render: (doc) => (
        doc.minio_key ? (
          <button
            onClick={() => handleDownloadPdf(doc)}
            className="text-red-600 hover:text-red-800"
            title="Télécharger le PDF"
          >
            <svg className="w-5 h-5 inline" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 18h12a2 2 0 002-2V6l-4-4H4a2 2 0 00-2 2v12a2 2 0 002 2zm5-4v-4h2v4h3l-4 4-4-4h3zM14 2l4 4h-4V2z" />
            </svg>
          </button>
        ) : (
          <span className="text-gray-300">—</span>
        )
      ),
    },
    {
      key: 'statut',
      header: 'Statut',
      align: 'center' as const,
      filterable: true,
      filterValueFn: (doc) => getStatut(doc),
      filterOptions: [
        { value: 'ok', label: 'OK' },
        { value: 'needs_review', label: 'À réviser' },
        { value: 'skip_benchmark', label: 'Exclu' },
      ],
      render: (doc) => (
        <div className="flex items-center justify-center gap-1">
          {isAdmin() ? (
            <>
              <button
                onClick={() => handleToggleFlag(doc, 'needs_review')}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                  doc.needs_review
                    ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                    : 'bg-gray-50 text-gray-400 hover:bg-orange-50 hover:text-orange-600'
                }`}
                title={doc.needs_review ? 'Retirer "À réviser"' : 'Marquer "À réviser"'}
              >
                À réviser
              </button>
              <button
                onClick={() => handleToggleFlag(doc, 'skip_benchmark')}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                  doc.skip_benchmark
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                }`}
                title={doc.skip_benchmark ? 'Inclure dans les benchmarks' : 'Exclure des benchmarks'}
              >
                Exclu
              </button>
            </>
          ) : (
            <>
              {doc.needs_review && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                  À réviser
                </span>
              )}
              {doc.skip_benchmark && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Exclu
                </span>
              )}
              {!doc.needs_review && !doc.skip_benchmark && (
                <span className="text-green-600 text-xs">OK</span>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Date création',
      sortable: true,
      searchable: true,
      sortValueFn: (doc) => doc.created_at,
      render: (doc) => new Date(doc.created_at).toLocaleDateString('fr-CA', {
        timeZone: 'America/Montreal',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    },
    {
      // Filtre sur la sélection courante (Cochés / Non cochés), juste avant les
      // actions. filterValueFn lit selectedKeys (recalculé à chaque render).
      key: 'selection',
      header: 'Sélection',
      align: 'center' as const,
      filterable: true,
      filterValueFn: (doc) => (selectedKeys.has(doc.id) ? 'checked' : 'unchecked'),
      filterOptions: [
        { value: 'checked', label: 'Cochés' },
        { value: 'unchecked', label: 'Non cochés' },
      ],
      render: (doc) =>
        selectedKeys.has(doc.id) ? (
          <span className="text-xs text-green-700">✓</span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (doc) => (
        <div className="flex items-center gap-1.5">
          {/* Voir */}
          <button
            onClick={() => setSelectedDoc(doc)}
            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
            title="Voir le document"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          {/* Corriger */}
          {isAdmin() && (
            <button
              onClick={() => navigate(`/benchmark/ground-truth/${doc.id}`)}
              className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
              title="Corriger (comparaison GT)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </button>
          )}

          {/* Éditer */}
          {isAdmin() && (
            <button
              onClick={() => setEditFieldDoc(doc)}
              className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
              title="Éditer un champ"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}

          {/* Supprimer */}
          {isSuperAdmin() && (
            <button
              onClick={() => handleDelete(doc)}
              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
              title="Supprimer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      ),
    },
  ], [allTags, allRunTags, selectedKeys, isAdmin, isSuperAdmin, navigate]);

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Documents sources (ground truth)</h1>
        {isSuperAdmin() && (
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Importer des documents
          </button>
        )}
      </div>

      {/* Toolbar: filter info (left) + bulk actions (right) */}
      {isAdmin() && (
        <div className="mb-3 p-3 rounded-lg border bg-gray-50 border-gray-200 flex items-center justify-between flex-wrap gap-2">
          {/* Left: document counts & filter info */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{totalCount} document(s)</span>
            {hasActiveTableFilters && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-blue-600">{filteredCount} affichés</span>
                <button
                  onClick={() => tableRef.current?.resetFilters()}
                  className="text-blue-600 hover:underline"
                >
                  Effacer filtres
                </button>
              </>
            )}
          </div>

          {/* Right: selection count + bulk actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-sm font-medium ${hasSelection ? 'text-blue-800' : 'text-gray-400'}`}>
              {hasSelection ? `${selectedKeys.size} sélectionné(s) :` : 'Aucune sélection'}
            </span>
            <button
              onClick={() => requestBulk((keys) => handleBulkFlag(keys, 'needs_review', true))}
              disabled={!hasSelection}
              className={`text-sm ${hasSelection ? 'text-orange-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Marquer à réviser
            </button>
            <button
              onClick={() => requestBulk((keys) => handleBulkFlag(keys, 'needs_review', false))}
              disabled={!hasSelection}
              className={`text-sm ${hasSelection ? 'text-gray-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Démarquer
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => requestBulk((keys) => handleBulkFlag(keys, 'skip_benchmark', true))}
              disabled={!hasSelection}
              className={`text-sm ${hasSelection ? 'text-yellow-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Exclure
            </button>
            <button
              onClick={() => requestBulk((keys) => handleBulkFlag(keys, 'skip_benchmark', false))}
              disabled={!hasSelection}
              className={`text-sm ${hasSelection ? 'text-gray-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
            >
              Inclure
            </button>
            <span className="text-gray-300">|</span>
            {/* PROD1.7.5 — Exporter la sélection en xlsx */}
            <button
              onClick={() => requestBulk(async (keys) => {
                try {
                  const ids = keys.map((k) => Number(k));
                  await groundTruthService.exportExcel(ids);
                  toast.success(`${ids.length} document(s) exporté(s)`);
                } catch (err: unknown) {
                  const message = err instanceof Error ? err.message : 'Erreur';
                  toast.error(message);
                }
              })}
              disabled={!hasSelection}
              className={`text-sm ${hasSelection ? 'text-blue-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
              title={hasSelection ? 'Télécharger les GT sélectionnés en xlsx' : 'Sélectionner au moins un document'}
            >
              Exporter (xlsx)
            </button>
            <span className="text-gray-300">|</span>
            <div ref={bulkTagRef} className="relative">
              <button
                onClick={() => hasSelection && setShowBulkTagDropdown(showBulkTagDropdown === 'add' ? null : 'add')}
                disabled={!hasSelection}
                className={`text-sm ${hasSelection ? 'text-indigo-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
              >
                + Tag
              </button>
              {showBulkTagDropdown === 'add' && hasSelection && (
                <div className="absolute z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg p-2 min-w-[200px]">
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={bulkTagInput}
                      onChange={(e) => setBulkTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const t = bulkTagInput; requestBulk((keys) => handleBulkAddTag(keys, t)); } }}
                      placeholder="Nom du tag"
                      className="flex-1 border rounded px-2 py-1 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={() => { const t = bulkTagInput; requestBulk((keys) => handleBulkAddTag(keys, t)); }}
                      className="px-2 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                    >
                      OK
                    </button>
                  </div>
                  {allTags.length > 0 && (
                    <div className="mt-1 max-h-32 overflow-y-auto">
                      {allTags.filter((t) => t.includes(bulkTagInput.toLowerCase())).map((tag) => (
                        <button
                          key={tag}
                          onClick={() => requestBulk((keys) => handleBulkAddTag(keys, tag))}
                          className="block w-full text-left px-2 py-1 text-sm hover:bg-indigo-50 rounded"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div ref={bulkRemoveTagRef} className="relative">
              <button
                onClick={() => hasSelectionWithTags && setShowBulkTagDropdown(showBulkTagDropdown === 'remove' ? null : 'remove')}
                disabled={!hasSelectionWithTags}
                className={`text-sm ${hasSelectionWithTags ? 'text-red-500 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
              >
                - Tag
              </button>
              {showBulkTagDropdown === 'remove' && hasSelectionWithTags && (
                <div className="absolute z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg min-w-[160px]">
                  {selectedDocsTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => requestBulk((keys) => handleBulkRemoveTag(keys, tag))}
                      className="block w-full text-left px-3 py-1.5 text-sm hover:bg-red-50"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isSuperAdmin() && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => requestBulk((keys) => handleBulkDelete(keys))}
                  disabled={!hasSelection}
                  className={`text-sm ${hasSelection ? 'text-red-600 hover:underline' : 'text-gray-300 cursor-not-allowed'}`}
                >
                  Supprimer
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          ref={tableRef}
          persistKey="benchmark-ground-truth"
          paginated
          data={documents}
          columns={columns}
          rowKey={(doc) => doc.id}
          defaultSort={{ key: 'filename', direction: 'asc' }}
          rowClassName={(doc) => doc.skip_benchmark ? 'text-gray-400' : ''}
          noDataMessage="Aucun document ground truth disponible"
          emptyMessage="Aucun document ne correspond aux filtres"
          selectable={isAdmin()}
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          keepSelectionOnFilter
          onFilteredCountChange={handleFilteredCountChange}
        />
      )}

      {/* Modal de portée bulk (sélection hors filtre) */}
      {bulkScopeModal}

      {/* Viewer modal */}
      {selectedDoc && (
        <GroundTruthViewerModal
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
        />
      )}

      {/* Import modal (superadmin only) */}
      {showImportModal && (
        <GroundTruthImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={loadDocuments}
        />
      )}

      {/* Edit field modal (admin+) */}
      {editFieldDoc && (
        <GroundTruthEditFieldModal
          isOpen={!!editFieldDoc}
          onClose={() => setEditFieldDoc(null)}
          document={editFieldDoc}
          onSaved={loadDocuments}
        />
      )}
    </PageContainer>
  );
};
